import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { palette, FONT } from '../../styles/tourTheme';
import { CANVA_MENU_GREEN } from '../../utils/hamburgerMenu';

/** REQ-104 accent: the Canva menu green already used by the Map / Calendar menu and event cards. */
export const FILTER_GREEN = CANVA_MENU_GREEN; // #2F9A84
// Body-size green text needs more contrast than the fill colour gives on white / charcoal.
export const FILTER_GREEN_TEXT_LIGHT = '#1F7563'; // 5.6:1 on #fffffe
export const FILTER_GREEN_TEXT_DARK = '#6CCDB6'; // 8.9:1 on #1b1b21

// Fills that carry white text use a deeper shade of the same green: white on #2F9A84 is 3.5:1,
// white on #23806D is 4.8:1 (AA for small text).
export const FILTER_GREEN_FILL = '#23806D';
export const FILTER_GREEN_FILL_HOVER = '#1F7563';

export const isWeb = Platform.OS === 'web';

export function filterTokens(isDarkMode) {
  const pal = palette(isDarkMode);
  return {
    pal,
    green: FILTER_GREEN,
    fill: FILTER_GREEN_FILL,
    fillHover: FILTER_GREEN_FILL_HOVER,
    greenText: isDarkMode ? FILTER_GREEN_TEXT_DARK : FILTER_GREEN_TEXT_LIGHT,
    greenWash: isDarkMode ? 'rgba(47,154,132,0.22)' : 'rgba(47,154,132,0.10)',
    surface: isDarkMode ? 'rgba(27,27,33,0.94)' : 'rgba(255,255,254,0.96)',
    panel: isDarkMode ? '#1b1b21' : '#fffffe',
    hover: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(20,20,26,0.05)',
    sans: FONT.sans,
    mono: FONT.mono,
  };
}

/** Visible keyboard focus ring (web only; RN-web Pressable reports `focused`). */
export function focusRing(focused, isDarkMode) {
  if (!isWeb || !focused) return null;
  return {
    outlineStyle: 'solid',
    outlineWidth: 2,
    outlineOffset: 2,
    outlineColor: isDarkMode ? FILTER_GREEN_TEXT_DARK : FILTER_GREEN,
  };
}

/** Short colour / transform transitions, none when the person asked for reduced motion. */
export function webTransition(reduced, props = 'background-color, border-color, color, transform, opacity') {
  if (!isWeb || reduced) return null;
  return { transitionProperty: props, transitionDuration: '160ms', transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)' };
}

export function prefersReducedMotionNow() {
  try {
    return isWeb && typeof window !== 'undefined'
      && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  } catch {
    return false;
  }
}

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(prefersReducedMotionNow);
  useEffect(() => {
    if (!isWeb || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}
